import { cutoutHeelPreset, cutoutMPJ1Preset, cutoutMPJ5Preset, cutoutMetabase5Preset, dwMetabase5Preset, dwNavicularPreset, rbwHourglassPreset, rbwLateralDeformityPreset, rbwMedialDeformityPreset, rbwStandardPreset, } from "../../tool-presets.js";
import { EnsureNumberOrNullish, EnsureStringOrNullish } from "../../util.js";
import { archModificationMap, archReinforcementMap, firstRayCutoutMap, flangeHeightMap, heelSeatMap, highArchHeightThreshold, lowArchHeightThreshold, maxExtensionThickness, minExtensionThickness, prescriptionSides, shellThicknessLookupTable, shoeWidthMap, unitMultiplierMap, weightRangeValues, } from "./values.js";
export function UpdateShellModifications(leftParams, leftFootScanParams, rightParams, rightFootScanParams, shellModifications, uiContextData) {
    UpdateWidthModification(leftParams, rightParams, uiContextData.shoeWidth);
    UpdateCutouts(leftParams, rightParams, shellModifications);
    UpdateDomesAndWells(leftParams, rightParams, shellModifications);
    UpdateRegionBasedWidth(leftParams, rightParams, shellModifications.shellShape);
    UpdateFirstRayCutout(leftParams, rightParams, shellModifications.firstRayCutout);
    UpdateHeelSeat(leftParams, rightParams, shellModifications.heelSeat);
    UpdateArchModification(leftParams, rightParams, shellModifications.archModification);
    UpdateArchReinforcement(leftParams, rightParams, shellModifications.archReinforcement);
    UpdateFlanges(leftParams, rightParams, shellModifications);
    UpdateSulcus(leftParams, rightParams, shellModifications.shellLength);
    // Must be after arch modifications
    UpdateShellThickness(leftParams, leftFootScanParams, rightParams, rightFootScanParams, shellModifications, uiContextData);
    // Must be after shell thickness
    UpdateMortonsExtension(leftParams, rightParams, shellModifications);
}
function UpdateForLeftAndRight(side, leftParams, rightParams, updater, disabled) {
    if (side === "left" || side === "both") {
        updater(leftParams, "left");
    }
    else {
        disabled === null || disabled === void 0 ? void 0 : disabled(leftParams, "left");
    }
    if (side === "right" || side === "both") {
        updater(rightParams, "right");
    }
    else {
        disabled === null || disabled === void 0 ? void 0 : disabled(rightParams, "right");
    }
}
function clamp(x, minX, maxX) {
    if (x < minX) {
        return minX;
    }
    if (x > maxX) {
        return maxX;
    }
    return x;
}
function getTargetArchHeight(archHeight, scanMode) {
    // See table: https://leopoly.atlassian.net/browse/HPINS-1361 or https://hp-ibdv.atlassian.net/browse/LEROY-194
    if (scanMode == "non-weighted") {
        if (archHeight >= 20.0) {
            archHeight = archHeight * (1.0 - 0.2);
        }
        else {
            archHeight = archHeight * (1.0 - 0.1);
        }
    }
    else if (scanMode == "foambox") {
        // No change
    } // scanMode == "semi-weighted" || scanMode == "weighted"
    else {
        if (archHeight >= 20.0) {
            archHeight = archHeight * (1.0 + 0.1);
        }
        else if (archHeight < 20.0 && archHeight >= 16.0) {
            archHeight = archHeight * (1.0 + 0.15);
        }
        else {
            archHeight = archHeight * (1.0 + 0.2);
        }
    }
    return clamp(archHeight, 10.0, 27.0);
}
function UpdateWidthModification(leftParams, rightParams, shoeWidths) {
    // HPINS-1380
    // Ignore shoeWidth Rx. Always set to standard ("widthModification": 0).
    // A new Rx will be created in the future to properly increase/decrease Shell Width by 1/8"
    // leftParams.widthModification = shoeWidthMap[shoeWidths.left];
    // rightParams.widthModification = shoeWidthMap[shoeWidths.right];
    leftParams.widthModification = shoeWidthMap["standard"];
    rightParams.widthModification = shoeWidthMap["standard"];
}
function UpdateShellThickness(leftParams, leftFootScanParams, rightParams, rightFootScanParams, shellModifications, uiContextData) {
    function GetArchHeightType(archHeight) {
        if (archHeight <= lowArchHeightThreshold) {
            return "Low";
        }
        else if (archHeight > highArchHeightThreshold) {
            return "High";
        }
        else {
            return "Average";
        }
    }
    function GetWeightRange(weight, unit) {
        const multiplier = unitMultiplierMap[unit];
        const weightValue = Number(weight) * multiplier;
        for (const range of weightRangeValues) {
            const [weightRange, maxWeight] = range;
            if (weightValue <= maxWeight) {
                return weightRange;
            }
        }
        return ">250";
    }
    function GetShellThicknessValue(rigidity, weightRange, archHeightType) {
        return shellThicknessLookupTable[archHeightType][rigidity][weightRange];
    }
    const rigidity = shellModifications.shellRigidity;
    const { value: weight, unit } = uiContextData.weight;
    const weightRange = GetWeightRange(weight, unit);
    UpdateForLeftAndRight("both", [leftParams, leftFootScanParams], [rightParams, rightFootScanParams], ([params, footScanParams]) => {
        var _a, _b;
        const archHeight = getTargetArchHeight((_a = EnsureNumberOrNullish(footScanParams === null || footScanParams === void 0 ? void 0 : footScanParams.archHeight)) !== null && _a !== void 0 ? _a : 15.0, (_b = EnsureStringOrNullish(footScanParams === null || footScanParams === void 0 ? void 0 : footScanParams.scanMode)) !== null && _b !== void 0 ? _b : "semi-weighted");
        const archHeightType = GetArchHeightType(archHeight);
        params.shellThickness = GetShellThicknessValue(rigidity, weightRange, archHeightType) * 0.1;
    });
    // When both left and right shells are generated, they should have the same thickness.
    // In the cases of different left/right arch height classification, both shells should default to the thicker of the two shells
    if (leftFootScanParams !== null && rightFootScanParams !== null) {
        leftParams.shellThickness = rightParams.shellThickness = Math.max(Number(leftParams.shellThickness), Number(rightParams.shellThickness));
    }
}
function UpdateCutouts(leftParams, rightParams, shellModifications) {
    const cutoutCounts = { left: 0, right: 0 };
    const prefixes = ["firstCutout", "secondCutout"];
    const maxCutoutCount = prefixes.length;
    for (const prefix of prefixes) {
        leftParams[`${prefix}Shape`] = "none";
        rightParams[`${prefix}Shape`] = "none";
    }
    function UpdateCutout(cutoutSide, data) {
        UpdateForLeftAndRight(cutoutSide, leftParams, rightParams, (params, side) => {
            const cutoutIndex = cutoutCounts[side]++;
            if (cutoutIndex >= maxCutoutCount) {
                throw new Error(`Too many cutouts for ${side} side`);
            }
            const prefix = prefixes[cutoutIndex];
            params[`${prefix}Shape`] = (() => {
                switch (data.shape) {
                    case "Ellipse":
                        return "ellipse";
                    default:
                        // Shouldn't happen
                        return "none";
                }
            })();
            params[`${prefix}PositionU`] = data.u;
            params[`${prefix}PositionV`] = data.v;
            params[`${prefix}SizeD1`] = data.d1;
            params[`${prefix}SizeD2`] = data.d2;
            params[`${prefix}Rotation`] = data.rotation;
        });
    }
    UpdateCutout(shellModifications.heelCutout, cutoutHeelPreset);
    UpdateCutout(shellModifications.cutoutFifth, cutoutMPJ5Preset);
    UpdateCutout(shellModifications.cutoutAndFirst, cutoutMPJ1Preset);
    UpdateCutout(shellModifications.cutoutAndStyloid, cutoutMetabase5Preset); // Cutout at Styloid Process = Cutout at 5th Metabase
}
function UpdateDomesAndWells(leftParams, rightParams, shellModifications) {
    const domeCounts = { left: 0, right: 0 };
    const prefixes = ["firstDome", "secondDome", "thirdDome", "fourthDome"];
    const maxDomeCount = prefixes.length;
    for (const prefix of prefixes) {
        leftParams[`${prefix}Shape`] = "none";
        rightParams[`${prefix}Shape`] = "none";
    }
    function Update(side, data) {
        UpdateForLeftAndRight(side, leftParams, rightParams, (params, side) => {
            const cutoutIndex = domeCounts[side]++;
            if (cutoutIndex >= maxDomeCount) {
                throw new Error(`Too many domes/wells for ${side} side`);
            }
            const prefix = prefixes[cutoutIndex];
            params[`${prefix}Shape`] = (() => {
                switch (data.shape) {
                    case "Ellipse":
                        return "ellipse";
                    default:
                        // Shouldn't happen
                        return "none";
                }
            })();
            params[`${prefix}PositionU`] = data.u;
            params[`${prefix}PositionV`] = data.v;
            params[`${prefix}Height`] = data.h;
            params[`${prefix}SizeD1`] = data.d1;
            params[`${prefix}SizeD2`] = data.d2;
            params[`${prefix}Rotation`] = data.rotation;
            params[`${prefix}SlopeS1`] = data.s1;
            params[`${prefix}SlopeS2`] = data.s2;
        });
    }
    Update(shellModifications.navicularWell, dwNavicularPreset);
    Update(shellModifications.wellAndStyloid, dwMetabase5Preset); // Well at Styloid Process = 5th Metabase Well
}
function UpdateFirstRayCutout(leftParams, rightParams, firstRayCutoutParams) {
    UpdateForLeftAndRight(firstRayCutoutParams.side, leftParams, rightParams, params => {
        params.firstRayCutoutEnabled = true;
        if (firstRayCutoutParams.value) {
            params.firstRayCutoutAngle = firstRayCutoutMap[firstRayCutoutParams.value];
        }
    }, params => (params.firstRayCutoutEnabled = false));
}
function UpdateHeelSeat(leftParams, rightParams, heelSeatHeight) {
    leftParams.heelSeatHeight = rightParams.heelSeatHeight = heelSeatMap[heelSeatHeight];
}
function UpdateArchModification(leftParams, rightParams, archModification) {
    leftParams.archModification = archModificationMap[archModification.left];
    rightParams.archModification = archModificationMap[archModification.right];
}
function UpdateArchReinforcement(leftParams, rightParams, archReinforcement) {
    // Note: this is the arch wedge tool, not the arch reinforcement tool
    UpdateForLeftAndRight(archReinforcement.side, leftParams, rightParams, params => {
        switch (archReinforcement.value) {
            case "1.5mm":
            case "3mm":
            case "6mm":
                params.archWedge = archReinforcementMap[archReinforcement.value];
                break;
            default:
                params.archWedge = null;
                break;
        }
        params.archWedgePatternEnabled = archReinforcement.pattern === "honeycomb";
    }, params => (params.archWedge = null));
}
const flangeData = [
    ["medialFlange", "medialFlangeHeight", "medialFlangeEnabled"],
    ["lateralFlange", "lateralFlangeHeight", "lateralFlangeEnabled"],
];
function UpdateFlanges(leftParams, rightParams, shellModifications) {
    for (const data of flangeData) {
        const [type, metadataHeightKey, metadataEnabledKey] = data;
        const flangeData = shellModifications[type];
        UpdateForLeftAndRight(flangeData.side, leftParams, rightParams, params => {
            params[metadataEnabledKey] = true;
            const height = flangeData.value;
            if (height !== undefined) {
                params[metadataHeightKey] = flangeHeightMap[height];
            }
        }, params => (params[metadataEnabledKey] = false));
    }
}
function UpdateMortonsExtension(leftParams, rightParams, shellModifications) {
    // Validate extension counts
    const extensionCounts = { left: 0, right: 0 };
    function UpdateExtensionCounts(ext) {
        if (ext.type === "none") {
            return;
        }
        const { side } = ext;
        const hasLeft = side === "left" || side === "both";
        const hasRight = side === "right" || side === "both";
        extensionCounts.left += hasLeft ? 1 : 0;
        extensionCounts.right += hasRight ? 1 : 0;
    }
    UpdateExtensionCounts(shellModifications.mortonsExtension);
    UpdateExtensionCounts(shellModifications.reverseMortonsExtension);
    for (const side of prescriptionSides) {
        if (extensionCounts[side] > 1) {
            throw new Error(`Cannot have more than one Morton\'s extension for ${side} foot`);
        }
    }
    leftParams.mortonsExtensionEnabled = false;
    leftParams.reverseMortonsExtensionEnabled = false;
    rightParams.mortonsExtensionEnabled = false;
    rightParams.reverseMortonsExtensionEnabled = false;
    function UpdateExtension(ext, isReverse) {
        if (ext.type === "none") {
            return;
        }
        const prefix = isReverse ? "reverseMortonsExtension" : "mortonsExtension";
        const { side, type, value } = ext;
        function UpdateForSide(currentSide, params) {
            if (side !== "both" && side !== currentSide) {
                return;
            }
            params[`${prefix}Enabled`] = true;
            params[`${prefix}Type`] = type;
            if (value !== undefined) {
                params[`${prefix}Thickness`] = (() => {
                    switch (value) {
                        case "consistent":
                            // When thickness is same as shell thickness and shell is <2.5mm or >4mm, keep Morton's thickness as 2.5mm or 4mm.
                            return Math.min(Math.max(Number(leftParams.shellThickness), minExtensionThickness), maxExtensionThickness);
                        case "2.5mm":
                            return 0.25;
                    }
                })();
            }
        }
        UpdateForSide("left", leftParams);
        UpdateForSide("right", rightParams);
    }
    UpdateExtension(shellModifications.mortonsExtension, false);
    UpdateExtension(shellModifications.reverseMortonsExtension, true);
}
function UpdateRegionBasedWidth(leftParams, rightParams, shellTrimline) {
    const presetData = (() => {
        switch (shellTrimline) {
            case "standard":
            default:
                return rbwStandardPreset;
            case "hourglass":
                return rbwHourglassPreset;
            case "medial-deformity":
                return rbwMedialDeformityPreset;
            case "lateral-deformity":
                return rbwLateralDeformityPreset;
        }
    })();
    for (const params of [leftParams, rightParams]) {
        params.regionBasedWidthProximalPoint = presetData.regionBasedWidthProximalPoint;
        params.regionBasedWidthDistalPoint = presetData.regionBasedWidthDistalPoint;
        params.regionBasedWidthWidth = presetData.regionBasedWidthWidth;
        params.regionBasedWidthSmoothing = presetData.regionBasedWidthSmoothing;
        params.regionBasedWidthLockDistal = presetData.regionBasedWidthLockDistal;
        params.regionBasedWidthMedialSideEnabled = presetData.regionBasedWidthMedialSideEnabled;
        params.regionBasedWidthLateralSideEnabled = presetData.regionBasedWidthLateralSideEnabled;
    }
}
function UpdateSulcus(leftParams, rightParams, shellLength) {
    switch (shellLength) {
        case "sulcus":
        case "full":
        case "met":
            leftParams.sulcusInsoleType = shellLength;
            rightParams.sulcusInsoleType = shellLength;
            break;
        default:
            leftParams.sulcusInsoleType = "met";
            rightParams.sulcusInsoleType = "met";
            break;
    }
}
//# sourceMappingURL=shell-modifications.js.map